import { useState, useCallback } from 'react';
import { Input, Spin, Tag, Alert, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import OPFPolicyPanel from './OPFPolicyPanel';
import { backendURL } from '../Globals';

// ISSN pattern: XXXX-XXXX
const ISSN_RE = /^\d{4}-\d{3}[\dXx]$/;

// ── Resultado de búsqueda por título (OPF response items) ───────────────────

const JournalCard = ({ item, onSelect }) => {
  const title = item.title?.find(t => t.preferred === 'yes')?.title
             || item.title?.[0]?.title
             || (typeof item.title === 'string' ? item.title : '—');
  const issns = (item.issns || []).map(i => i.issn).join(' · ');
  const pubRaw = item.publishers?.[0]?.publisher?.name || item.publisher?.name || item.publisher || '';
  const pub = typeof pubRaw === 'string' ? pubRaw : '';

  return (
    <div
      onClick={() => onSelect(item)}
      style={{
        padding: '10px 14px',
        border: '1px solid #e8e8e8',
        borderRadius: 6,
        background: '#fff',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {issns && <Tag style={{ fontFamily: 'monospace', fontSize: 11 }}>{issns}</Tag>}
        {pub   && <Tag color="default" style={{ fontSize: 11 }}>{pub}</Tag>}
      </div>
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────

const PolicyComponent = () => {
  const [query,       setQuery]       = useState('');
  const [searching,   setSearching]   = useState(false);
  const [results,     setResults]     = useState(null);   // array de items OPF
  const [searchErr,   setSearchErr]   = useState(null);

  const [policyData,  setPolicyData]  = useState(null);
  const [policyLoad,  setPolicyLoad]  = useState(false);
  const [policyErr,   setPolicyErr]   = useState(null);
  const [policyTitle, setPolicyTitle] = useState('');

  // ── Llama al backend para obtener política por ISSN ──────────────────────
  const fetchByISSN = useCallback(async (issn, displayTitle) => {
    setPolicyLoad(true);
    setPolicyData(null);
    setPolicyErr(null);
    setPolicyTitle(displayTitle || issn);
    setResults(null);
    try {
      const res  = await fetch(`${backendURL}/opf/policy?issn=${encodeURIComponent(issn)}`);
      const json = await res.json();
      if (!res.ok) setPolicyErr(json.error || 'Error desconocido');
      else setPolicyData(json);
    } catch {
      setPolicyErr('No se pudo conectar con el servidor');
    } finally {
      setPolicyLoad(false);
    }
  }, []);

  // ── Búsqueda por título en OPF ────────────────────────────────────────────
  const fetchByTitle = useCallback(async (q) => {
    setSearching(true);
    setResults(null);
    setSearchErr(null);
    setPolicyData(null);
    setPolicyErr(null);
    try {
      const res  = await fetch(`${backendURL}/opf/search?q=${encodeURIComponent(q)}`);
      const json = await res.json();
      const items = json.items || [];
        if (!res.ok) {
          setSearchErr(json.error || 'Error desconocido');
        } else if (items.length === 1) {
          // Auto-seleccionar si hay un único resultado
          const item = items[0];
          const firstISSN = item.issns?.[0]?.issn;
          const title = item.title?.find(t => t.preferred === 'yes')?.title || item.title?.[0]?.title || '';
          if (firstISSN) {
            fetchByISSN(firstISSN, title);
          } else {
            setResults(items);
          }
        } else {
          setResults(items);
        }
    } catch {
      setSearchErr('No se pudo conectar con el servidor');
    } finally {
      setSearching(false);
    }
  }, []);

  // ── Acción principal al pulsar Enter / lupa ───────────────────────────────
  const handleSearch = useCallback((val) => {
    const q = val.trim();
    if (!q) return;
    if (ISSN_RE.test(q)) {
      fetchByISSN(q, q);
    } else {
      fetchByTitle(q);
    }
  }, [fetchByISSN, fetchByTitle]);

  // ── Seleccionar una revista de los resultados ─────────────────────────────
  const handleSelect = useCallback((item) => {
    const firstISSN = item.issns?.[0]?.issn;
    const title     = item.title?.find(t => t.preferred === 'yes')?.title
                   || item.title?.[0]?.title || '';
    if (!firstISSN) {
      setPolicyErr('Esta revista no tiene ISSN registrado en Open Policy Finder.');
      setPolicyData(null);
      setPolicyTitle(title);
      setResults(null);
      return;
    }
    fetchByISSN(firstISSN, title);
  }, [fetchByISSN]);

  // ────────────────────────────────────────────────────────────────────────────

  const showPolicy  = policyLoad || policyData || policyErr;
  const showResults = !showPolicy && (searching || results !== null || searchErr);

  return (
    <div style={{ textAlign: 'left' }}>

      {/* Header */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center', background: '#fafafa' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Política de autoarchivo de revistas
        </div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 6, paddingBottom: 20 }}>
          Consulta si puedes depositar tu artículo en el repositorio institucional · Datos: Open Policy Finder (JISC)
        </div>
      </div>

      {/* Buscador */}
      <div style={{ maxWidth: 680, margin: '32px auto 24px', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Input
            size="middle"
            placeholder="Nombre de revista o ISSN (ej. 0028-0836)…"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setSearchErr(null);
              setPolicyErr(null);
            }}
            onClear={() => {
              setQuery('');
              setSearchErr(null);
              setResults(null);
              setPolicyData(null);
              setPolicyErr(null);
            }}
            onPressEnter={e => handleSearch(e.target.value)}
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            allowClear
            style={{ flex: 1, borderRadius: 8, fontSize: 14 }}
          />
          <Button
            size="middle"
            type="primary"
            onClick={() => handleSearch(query)}
            style={{ borderRadius: 8, flexShrink: 0 }}
          >
            Consultar
          </Button>
        </div>
        <div style={{ fontSize: 11, color: '#bbb', marginTop: 8, textAlign: 'center' }}>
          Si introduces un ISSN (formato XXXX-XXXX) se hace una búsqueda exacta.
          Con texto se busca por título en Open Policy Finder (se admiten términos parciales, ej. "nature").
        </div>
      </div>

      <div style={{ padding: '0 48px 48px', maxWidth: 900, margin: '0 auto' }}>

        {/* Resultados de búsqueda por título */}
        {showResults && (
          <div>
            {searching && (
              <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
            )}
            {searchErr && (
              <div style={{ color: '#ff4d4f', fontSize: 13, padding: '8px 0' }}>{searchErr}</div>
            )}
            {results !== null && !searching && (
              <>
                {results.length === 0 ? (
                  <div style={{ color: '#888', fontSize: 13 }}>
                    No se encontraron revistas con ese nombre en Open Policy Finder.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 12, color: '#aaa', marginBottom: 4 }}>
                      {results.length} resultado{results.length !== 1 ? 's' : ''} — haz clic en una revista para ver su política
                    </div>
                    {results.map((item, i) => (
                      <JournalCard key={item.system_metadata?.id || i} item={item} onSelect={handleSelect} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Panel de política */}
        {showPolicy && (
          <div>
            {policyTitle && (
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 14 }}>
                {policyTitle}
              </div>
            )}
            <OPFPolicyPanel
              loading={policyLoad}
              error={policyErr}
              data={policyData}
            />
          </div>
        )}


      </div>
    </div>
  );
};

export default PolicyComponent;
